import { Link } from '@tanstack/react-router';
import { useState, type ReactNode } from 'react';
import { type OutOutcome, type OutRow, type StageBarOut } from '@nest-recruit/shared';
import { Button } from '../../../../components/Button';
import { Drawer } from '../../../../components/Drawer';
import { Segmented } from '../../../../components/Segmented';
import { SkeletonLines } from '../../../../components/Skeleton';
import { ServerErrorState } from '../../../../components/system-states/SystemState';
import { STAGE_TONES } from '../../../../components/status-tones';
import { ToneChip } from '../../../../components/Tone';
import { formatDateTimeLong, formatNumber, formatRelative } from '../../../../lib/format';
import { useJobOut } from '../../api/jobs-api';
import styles from './OutPanel.module.css';
import { useStageLabels } from '../../../../lib/modules';

// "Out: N" (§L33): the job's rejected, withdrawn and closed, each with its reason, where
// they left from, who moved them and when. A side panel, so the stage behind stays put.

type OutFilter = OutOutcome | 'all';

const OUTCOME_LABELS: Readonly<Record<OutOutcome, string>> = {
  rejected: 'Rejected',
  withdrawn: 'Withdrawn',
  closed: 'Job closed',
};

const OUTCOME_STAGES = { rejected: 'rejected', withdrawn: 'withdrawn', closed: 'closed_job' } as const;

interface OutPanelProps {
  readonly jobId: string;
  readonly out: StageBarOut;
  readonly isOpen: boolean;
  readonly onClose: () => void;
}

export function OutPanel({ jobId, out, isOpen, onClose }: OutPanelProps): ReactNode {
  const [filter, setFilter] = useState<OutFilter>('all');
  const [page, setPage] = useState(1);
  const list = useJobOut(jobId, page, filter === 'all' ? undefined : filter, isOpen);
  const pages = list.data ? Math.max(1, Math.ceil(list.data.total / list.data.pageSize)) : 1;

  function choose(next: OutFilter): void {
    setFilter(next);
    setPage(1);
  }

  return (
    <Drawer
      isOpen={isOpen}
      onClose={onClose}
      title="Out of this job"
      meta={`${formatNumber(out.total)} ${out.total === 1 ? 'person' : 'people'}`}
      footer={
        pages > 1 ? (
          <div className={styles.pager}>
            <Button variant="outline" size="sm" disabled={page === 1} onClick={() => setPage(page - 1)}>
              Previous
            </Button>
            <span className={styles.pageOf}>
              Page {formatNumber(page)} of {formatNumber(pages)}
            </span>
            <Button variant="outline" size="sm" disabled={page >= pages} onClick={() => setPage(page + 1)}>
              Next
            </Button>
          </div>
        ) : undefined
      }
    >
      <div className={styles.body}>
        <Segmented<OutFilter>
          label="Show"
          value={filter}
          onChange={choose}
          options={[
            { value: 'all', label: `All ${formatNumber(out.total)}` },
            { value: 'rejected', label: `Rejected ${formatNumber(out.rejected)}` },
            { value: 'withdrawn', label: `Withdrawn ${formatNumber(out.withdrawn)}` },
            { value: 'closed', label: `Job closed ${formatNumber(out.closed)}` },
          ]}
        />
        <OutList list={list} />
      </div>
    </Drawer>
  );
}

function OutList({ list }: { readonly list: ReturnType<typeof useJobOut> }): ReactNode {
  if (list.error && !list.data) return <ServerErrorState onRetry={() => void list.refetch()} />;
  if (!list.data) return <SkeletonLines count={6} />;
  if (list.data.rows.length === 0) return <p className={styles.empty}>Nobody has left this job this way.</p>;
  return (
    <ul className={styles.list}>
      {list.data.rows.map((row) => (
        <OutItem key={row.applicationId} row={row} />
      ))}
    </ul>
  );
}

function OutItem({ row }: { readonly row: OutRow }): ReactNode {
  const stageLabels = useStageLabels();
  return (
    <li className={styles.item}>
      <div className={styles.itemHead}>
        <Link to="/applications/$applicationId" params={{ applicationId: row.applicationId }} className={styles.name}>
          {row.candidate.fullName || '—'}
        </Link>
        <ToneChip tone={STAGE_TONES[OUTCOME_STAGES[row.outcome]]}>{OUTCOME_LABELS[row.outcome]}</ToneChip>
      </div>
      <p className={styles.reason}>{row.reasonLabel ?? '—'}</p>
      {row.note && <p className={styles.note}>{row.note}</p>}
      <p className={styles.meta}>
        {[
          row.leftFrom ? `From ${stageLabels[row.leftFrom]}` : null,
          row.isSystem ? 'By the system' : row.byName ? `By ${row.byName}` : null,
        ]
          .filter(Boolean)
          .join(' · ')}
        {row.at && (
          <>
            {' · '}
            <time dateTime={row.at} title={formatDateTimeLong(row.at)}>
              {formatRelative(row.at)}
            </time>
          </>
        )}
      </p>
    </li>
  );
}
